/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
  CForm,
  CRow,
  CCol,
  CFormInput,
  CFormLabel,
  CFormTextarea,
  CFormSelect,
  CButton,
  CSpinner,
  CAlert,
  CTooltip,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilSave, cilX, cilInfo } from '@coreui/icons';
import PropTypes from 'prop-types';
import { useTranslation } from '../../hooks/useTranslation';

const YarnInputs = ({
  onSubmit,
  onCancel,
  isLoading = false,
  submitResponse = null,
  initialData = null,
  isEdit = false
}) => {
  const [formData, setFormData] = useState({
    yarnCode: '',
    yarnName: '',
    yarnType: '',
    yarnDescription: ''
  });

  const [errors, setErrors] = useState({});
  const [calculatedFactor, setCalculatedFactor] = useState(null);
  const { t } = useTranslation();

  const yarnTypeOptions = [
    { value: '1', label: t('yarnTypeNe'), description: t('yarnTypeNeDesc') },
    { value: '2', label: t('yarnTypeNm'), description: t('yarnTypeNmDesc') },
    { value: '3', label: t('yarnTypeDny'), description: t('yarnTypeDnyDesc') }
  ];

  const formatToTwoDecimals = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return num.toFixed(2);
  };

  useEffect(() => {
    if (initialData) {
      setFormData({
        yarnCode: initialData.YarnCode || '',
        yarnName: initialData.YarnName || '',
        yarnType: initialData.YarnType || '',
        yarnDescription: initialData.YarnDescription || ''
      });
      
      // Initial data varsa yarn code hesaplamasını yap
      if (initialData.YarnCode) {
        calculateYarnFactor(initialData.YarnCode, initialData.YarnType);
      }
    }
  }, [initialData]);

  useEffect(() => {
    if (submitResponse?.Result?.ErrorCode === 0) {
      setTimeout(() => {
        if (!isEdit) {
          setFormData({ 
            yarnCode: '', 
            yarnName: '', 
            yarnType: '', 
            yarnDescription: '' 
          });
        }
      }, 2000);
    }
  }, [submitResponse, isEdit]);

  const calculateYarnFactor = (value, type) => {
    if (!value || !type) {
      setCalculatedFactor(null);
      return;
    }

    let result = null;

    switch (type) {
      case '1': // Ne
        const nePattern = /^(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)$/;
        const neMatch = value.match(nePattern);
        if (neMatch) {
          const numerator = parseFloat(neMatch[1]);
          const denominator = parseFloat(neMatch[2]);
          if (denominator !== 0) {
            result = numerator / denominator;
          }
        }
        break;

      case '2': // Nm
        const nmPattern = /^(\d+(?:\.\d+)?)\s*nm$/i;
        const nmMatch = value.match(nmPattern);
        if (nmMatch) {
          const nmValue = parseFloat(nmMatch[1]);
          result = nmValue / 1.693;
        }
        break;

      case '3': // Dny (Denye)
        // Handle both 2-part (150/288) and 3-part (150/288/70) formats
        const dnyPattern2Part = /^(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)$/;
        const dnyPattern3Part = /^(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)$/;
        
        const dnyMatch3 = value.match(dnyPattern3Part);
        const dnyMatch2 = value.match(dnyPattern2Part);
        
        if (dnyMatch3) {
          // 3-part format: firstPart/filamentCount/thirdPart
          const firstPart = parseFloat(dnyMatch3[1]);      // Polyester/Nylon denier
          // Second part (filament count) is not used in calculation
          const thirdPart = parseFloat(dnyMatch3[3]);      // Lycra/Elastan denier
          
          let divisor;
          if (thirdPart >= 70) {
            divisor = firstPart + (thirdPart / 3.5);
          } else {
            divisor = firstPart + (thirdPart / 2.5);
          }
          
          if (divisor !== 0) {
            result = 5316 / divisor;
          }
        } else if (dnyMatch2) {
          // 2-part format: firstPart/secondPart
          const firstPart = parseFloat(dnyMatch2[1]);
          if (firstPart !== 0) {
            result = 5316 / firstPart;
          }
        }
        break;

      default:
        break;
    }

    if (result !== null && !isNaN(result)) {
      setCalculatedFactor(formatToTwoDecimals(result));
    } else {
      setCalculatedFactor(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Yarn Code veya Yarn Type değiştiğinde hesaplama yap
    if (name === 'yarnCode' || name === 'yarnType') {
      const codeValue = name === 'yarnCode' ? value : formData.yarnCode;
      const typeValue = name === 'yarnType' ? value : formData.yarnType;
      calculateYarnFactor(codeValue, typeValue);
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.yarnCode.trim()) {
      newErrors.yarnCode = t('Yarn Code Required');
    }
    if (!formData.yarnType) {
      newErrors.yarnType = t('Yarn Type Required');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const sessionUserId = localStorage.getItem('sessionUserId');
    if (!sessionUserId) {
      setErrors({ general: t('User session not found. Please log in again.') });
      return;
    }

    const payload = {
      SessionUserId: sessionUserId,
      YarnCode: formData.yarnCode.trim(),
      YarnName: formData.yarnName.trim(),
      YarnType: formData.yarnType,
      YarnDescription: formData.yarnDescription.trim(),
    };

    if (isEdit && initialData?.YarnId) {
      payload.YarnId = initialData.YarnId;
    }

    onSubmit(payload);
  };

  const handleCancel = () => {
    if (!isEdit) {
      setFormData({ 
        yarnCode: '', 
        yarnName: '', 
        yarnType: '', 
        yarnDescription: '' 
      });
    }
    setErrors({});
    onCancel();
  };

  return (
    <div>
      {errors.general && <CAlert color="danger">{errors.general}</CAlert>}
      {submitResponse?.Result && submitResponse.Result.ErrorCode !== 0 && (
        <CAlert color="danger">
          {submitResponse.Result?.ErrorDescription || t('anErrorOccurred')}
        </CAlert>
      )}

      <CForm onSubmit={handleSubmit}>
        <CRow className="mb-3">
          <CCol md={4}>
            <CFormLabel htmlFor="yarnCode" className="d-flex align-items-center">
              {t('yarnCode')}
              <CTooltip
                content={
                  <div>
                    <div className="fw-semibold mb-1">{t('sampleFormats')}</div>
                    <div>• {t('sampleFormatNe')}</div>
                    <div>• {t('sampleFormatNm')}</div>
                    <div>• {t('sampleFormatDny')}</div>
                  </div>
                }
                placement="top"
              >
                <CIcon 
                  icon={cilInfo} 
                  className="ms-2 text-info cursor-pointer"
                  style={{ 
                    fontSize: '0.875rem',
                    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))'
                  }}
                />
              </CTooltip>
            </CFormLabel>
            <div className="d-flex align-items-center gap-2">
              <CFormInput
                type="text"
                id="yarnCode"
                name="yarnCode"
                value={formData.yarnCode}
                onChange={handleInputChange}
                invalid={!!errors.yarnCode}
                disabled={isLoading}
                placeholder={t('enterYarnCode')}
                style={{ flex: 1 }}
              />
              {calculatedFactor && (
                <span 
                  className="badge bg-success rounded-pill d-inline-flex align-items-center"
                  style={{ 
                    fontSize: '0.85rem',
                    padding: '0.375rem 0.75rem',
                    fontWeight: '500',
                    whiteSpace: 'nowrap'
                  }}
                >
                  = {calculatedFactor}
                </span>
              )}
            </div>
            {errors.yarnCode && <div className="invalid-feedback d-block">{errors.yarnCode}</div>}
          </CCol>

          <CCol md={4}>
            <CFormLabel htmlFor="yarnName">{t('yarnName')}</CFormLabel>
            <CFormInput
              type="text"
              id="yarnName"
              name="yarnName"
              value={formData.yarnName}
              onChange={handleInputChange}
              invalid={!!errors.yarnName}
              disabled={isLoading}
              placeholder={t('enterYarnName')}
            />
            {errors.yarnName && <div className="invalid-feedback d-block">{errors.yarnName}</div>}
          </CCol>

          <CCol md={4}>
            <CFormLabel htmlFor="yarnType">{t('yarnType')}</CFormLabel>
            <CFormSelect
              id="yarnType"
              name="yarnType"
              value={formData.yarnType}
              onChange={handleInputChange}
              invalid={!!errors.yarnType}
              disabled={isLoading || isEdit}
              required
            >
              <option value="">{t('selectYarnType')}</option>
              {yarnTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </CFormSelect>
            {formData.yarnType && (
              <small className="text-muted">
                {yarnTypeOptions.find(opt => opt.value === formData.yarnType)?.description}
              </small>
            )}
            {errors.yarnType && <div className="invalid-feedback d-block">{errors.yarnType}</div>}
          </CCol>
        </CRow>

        <CRow className="mb-3">
          <CCol md={12}>
            <CFormLabel htmlFor="yarnDescription">{t('Yarn Properties')}</CFormLabel>
            <CFormTextarea
              id="yarnDescription"
              name="yarnDescription"
              rows={4}
              value={formData.yarnDescription}
              onChange={handleInputChange}
              invalid={!!errors.yarnDescription}
              disabled={isLoading}
              placeholder={t('Enter Yarn Properties')}
            />
            {errors.yarnDescription && (
              <div className="invalid-feedback d-block">{errors.yarnDescription}</div>
            )}
          </CCol>
        </CRow>

        <CRow>
          <CCol md={12} className="d-flex gap-2">
            <CButton type="submit" color="primary" disabled={isLoading}>
              {isLoading ? (
                <>
                  <CSpinner size="sm" className="me-2" />
                  {t('Saving')}
                </>
              ) : (
                <>
                  <CIcon icon={cilSave} className="me-2" /> {isEdit ? t('updateYarn') : t('createYarn')}
                </>
              )}
            </CButton>
            <CButton color="secondary" variant="outline" onClick={handleCancel} disabled={isLoading}>
              <CIcon icon={cilX} className="me-2" /> {t('Cancel')}
            </CButton>
          </CCol>
        </CRow>
      </CForm>
    </div>
  );
};

YarnInputs.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  submitResponse: PropTypes.object,
  initialData: PropTypes.object,
  isEdit: PropTypes.bool,
};

export default YarnInputs;